/**
 * cycle-phase.js — the halving clock, as approved in K3 on 2026-08-11.
 *
 * WHY THIS EXISTS
 * The bonus engine's only crypto signal was `deepLosers`, ranked by DRAWDOWN DEPTH. That makes
 * the worst-performing coin the biggest recommendation, which is averaging down as a rule and
 * which directly contradicted the owner's own approved plan: K3 gates XRP adds on a weekly MM
 * turn, and XRP was the name the engine put first because it had fallen furthest ($326 position,
 * -29.3%). The plan was approved 2026-08-11 with a binding condition that the Bonus page reflect
 * it, and a 28 Aug deadline. It was never wired. This module is the wire.
 *
 * WHAT IS ENCODED HERE IS THE RECORD, NOT A FORECAST. Every constant traces to K3. The caveats
 * K3 put on its own record travel with the output rather than being dropped at the boundary:
 * n=3 completed cycles, a publicly known pattern (so early/late-bottom risk is real), amplitude
 * diminishing cycle over cycle, and ETH/XRP riding BTC's correlation rather than leading it.
 *
 * Pure: no imports, no state, no network, no clock of its own — the caller passes the month.
 */

/** Halving months. The 2028 entry is PROJECTED and says so; K3's 2029 sell window depends on it. */
export const HALVINGS = [
  { month: '2012-11', observed: true },
  { month: '2016-07', observed: true },
  { month: '2020-05', observed: true },
  { month: '2024-04', observed: true },
  { month: '2028-04', observed: false },
];

/** K3: tops H+13/17/18/18 · bottoms H+26/29/30. Windows are the actionable spans around them. */
export const WINDOWS = { distribute: [12, 18], accumulate: [26, 31] };

export const CAVEATS = [
  'n=3 completed cycles',
  'publicly known pattern — early or late bottom is a live risk',
  'amplitude diminishing cycle over cycle',
  'ETH and XRP ride the correlation rather than lead it',
];

/** K3 entry discipline and invalidations, verbatim. Weekly CLOSE, not an intraday print. */
/**
 * THE PRICE ENTRY GATES ARE GONE. THE INVALIDATION STOPS ARE NOT.
 *
 * K3 carried two entry gates on BTC: an entry zone at $66,000 and a channel floor at $70,300,
 * both meaning "do not buy above this". On 2026-09-03 BTC was $77,871 and the owner decided to
 * buy anyway, on the halving clock rather than the level. That is a legitimate call, and the
 * only unacceptable outcome was leaving the gates in place while acting against them: the app
 * would report BTC blocked every month while the book filled with BTC, and the record and the
 * behaviour would diverge. His words: the phase window is enough.
 *
 * So entry is now governed by ONE thing, `cyclePhase` — accumulate at H+26..31, distribute at
 * H+12..18, hold otherwise. Removing the floor without removing the entry zone would have
 * changed nothing, because $77,871 breaches both; both go, or neither does.
 *
 * `invalidations` STAY, and they are not the same kind of rule. An entry gate is an opinion
 * about price; an invalidation is a stop that says the thesis is broken. He did not ask to
 * remove those and they are the only brake left on a sleeve that no longer has a ceiling
 * either. `integrity.cycleBrakes` grades whether they can actually fire. (2026-09-03)
 */
export const GATES = {
  invalidations: { BTC: 52500, ETH: 1384, XRP: 0.87 },
};

/** K3 two-phase plan. XRP is strictly conditional; unspent XRP redistributes 60/40 BTC/ETH. */
export const PLAN = {
  // `usd` is ALWAYS the sum of `split`, so the amount and the legs can never disagree.
  // `recordedUsd` is the headline figure K3's own table states. For phase 2 the two differ by
  // $15 (2735+2590+230 = 5555, stated 5540); the record disagrees with itself and the module
  // reports the gap rather than quietly choosing a side. (round 2, 2026-09-02)
  phase1: { months: ['2026-09', '2026-10', '2026-11'], usd: 5350, recordedUsd: 5350, split: { BTC: 2800, ETH: 2300, XRP: 250 }, deadline: '2026-11-30', tail: false },
  phase2: { months: ['2026-12', '2027-01', '2027-02'], usd: 5555, recordedUsd: 5540, split: { BTC: 2735, ETH: 2590, XRP: 230 }, deadline: null, tail: true },
  xrpRedistribution: { BTC: 0.6, ETH: 0.4 },
};

/** Where the record and its own arithmetic disagree. Empty is a real answer here. */
export function planDiscrepancies() {
  return ['phase1', 'phase2'].map(k => {
    const p = PLAN[k];
    const sum = Object.values(p.split).reduce((a, b) => a + b, 0);
    return (p.recordedUsd != null && p.recordedUsd !== sum)
      ? { phase: k, statedUsd: p.recordedUsd, splitSum: sum, delta: +(sum - p.recordedUsd).toFixed(2) } : null;
  }).filter(Boolean);
}

/**
 * Which phase, if any, NAMES this month. K3's phase 2 runs Dec-Feb, which is H+32..34 and
 * therefore outside `WINDOWS.accumulate` -- deliberately, because K3 says "tail extends via
 * phase 2". So the plan's month list is the gate, and the window only decides whether that
 * month is inside the observed bottom or on the declared tail.
 */
export function planForMonth(monthKey) {
  const key = ['phase1', 'phase2'].find(k => PLAN[k].months.includes(String(monthKey)));
  return key ? { key, ...PLAN[key] } : null;
}

const _idx = (monthKey) => {
  const m = /^(\d{4})-(\d{2})$/.exec(String(monthKey || ''));
  if (!m) return null;
  const mo = Number(m[2]);
  if (mo < 1 || mo > 12) return null;
  return Number(m[1]) * 12 + (mo - 1);
};

/** Months since the most recent halving at or before `monthKey`. Null before the first halving. */
export function monthsSinceHalving(monthKey) {
  const t = _idx(monthKey);
  if (t == null) return null;
  let best = null;
  HALVINGS.forEach(hv => {
    const hi = _idx(hv.month);
    if (hi == null || hi > t) return;
    if (!best || hi > best.i) best = { i: hi, hv };
  });
  if (!best) return null;
  return { h: t - best.i, halving: best.hv.month, observed: best.hv.observed };
}

/**
 * The phase for a month. 'accumulate' | 'distribute' | 'hold', or null when the month predates
 * the first halving — null is reportable, never a silent 'hold'.
 */
export function cyclePhase(monthKey) {
  const s = monthsSinceHalving(monthKey);
  if (!s) return null;
  const inWin = (w) => s.h >= w[0] && s.h <= w[1];
  const phase = inWin(WINDOWS.accumulate) ? 'accumulate' : inWin(WINDOWS.distribute) ? 'distribute' : 'hold';
  return {
    month: monthKey, phase, h: s.h, halving: s.halving, halvingObserved: s.observed,
    window: phase === 'hold' ? null : WINDOWS[phase],
    // A phase resting on a PROJECTED halving is a weaker claim than one resting on an observed
    // one, and the difference must reach the screen rather than being flattened into a label.
    confidence: s.observed ? 'observed_halving' : 'projected_halving',
    caveats: CAVEATS,
  };
}

/**
 * Gate check against live weekly-close prices. Returns what is BLOCKED and why — an empty
 * `blocked` array is only meaningful when `priced` lists the coins actually checked.
 */
export function evaluateGates(closes, opts = {}) {
  const px = closes || {};
  const priced = [], blocked = [], invalidated = [];
  Object.keys(GATES.invalidations).forEach(k => {
    const v = Number(px[k]);
    if (!isFinite(v) || v <= 0) return;
    priced.push(k);
    if (v < GATES.invalidations[k]) invalidated.push({ ticker: k, close: v, level: GATES.invalidations[k] });
  });
  // No price may block an entry any more. `blocked` survives as a shape because the XRP gate
  // and any future stop still speak through it, and because `cycleAllocation` holds a blocked
  // coin's money rather than redistributing it. It is simply empty on price grounds now.
  // K3: no XRP add until the weekly MM bars turn green. That is an eyes-on confirmation the app
  // cannot compute, so it is an explicit input and its ABSENCE means NOT armed, never armed.
  const xrpArmed = opts.xrpConfirmed === true;
  // An empty `blocked` list means "nothing was blocked", NOT "everything was checked".
  // Measured in round 1: evaluateGates({}) returned a clean sheet byte-identical to a fully
  // priced pass, so a failed price refresh emitted the full claim with the entry gate never
  // applied and the invalidation stop unable to fire. `unpriced` is now part of the answer.
  const unpriced = Object.keys(GATES.invalidations).filter(k => !priced.includes(k));
  return {
    priced, unpriced, blocked, invalidated, xrpArmed,
    allStop: invalidated.length > 0,
    // Now that the price entry gates are gone, BTC's close is needed for ONE thing: the
    // invalidation stop at $52,500. That is the last brake on this sleeve, so its absence is
    // still a refusal and not a warning. The name is kept because `cycleAllocation` and its
    // tests read it, but what it certifies has changed: not "entry is checkable" but "the stop
    // can fire". (2026-09-03)
    entryCheckable: priced.includes('BTC'),
  };
}

/**
 * Split an amount across BTC/ETH/XRP by the K3 phase ratios, honouring the XRP gate.
 * Returns null when the phase is not 'accumulate' — there is no crypto buy outside the window,
 * and returning a zeroed split instead would read as "the plan says nothing", which is a lie.
 */
export function cycleAllocation(amountUSD, phaseResult, gates, phaseKey = 'phase1') {
  const amt = Number(amountUSD);
  if (!isFinite(amt) || amt <= 0) return null;
  if (!phaseResult) return null;
  const plan = PLAN[phaseKey] || PLAN.phase1;
  // A plan month on the declared tail (phase 2) is legitimate even though the halving window
  // has closed; anywhere else, the window governs.
  const onPlanMonth = plan.months.includes(String(phaseResult.month));
  if (phaseResult.phase !== 'accumulate' && !(plan.tail && onPlanMonth)) return null;
  const g = gates || {};
  if (g.allStop) return { amount: 0, split: {}, stopped: true, reason: 'invalidation_hit', invalidated: g.invalidated || [] };
  // No BTC price means the entry zone and the channel floor were never applied. Emitting the
  // claim anyway is the silent degradation this module exists to prevent.
  // `=== false` let a hand-built or omitted gates object through as "checkable", which is round
  // 1's finding moved up one boundary. Absence means NOT checkable, exactly as it means NOT
  // armed for xrpConfirmed. (round 3, D10)
  if (g.entryCheckable !== true) {
    return { amount: 0, split: {}, stopped: true, reason: 'gates_uncheckable', unpriced: g.unpriced || [] };
  }

  const base = plan.split;
  const total = Object.values(base).reduce((a, b) => a + b, 0);
  const share = {};
  Object.keys(base).forEach(k => { share[k] = base[k] / total; });

  if (!g.xrpArmed) {
    const x = share.XRP || 0;
    share.XRP = 0;
    share.BTC = (share.BTC || 0) + x * PLAN.xrpRedistribution.BTC;
    share.ETH = (share.ETH || 0) + x * PLAN.xrpRedistribution.ETH;
  }
  // A blocked coin is not redistributed: K3 blocks BTC on PRICE, and moving that money to ETH
  // would be a different decision than the one that was approved. It is held instead.
  const blockedSet = new Set((g.blocked || []).map(b => b.ticker));
  let held = 0;
  const split = {};
  Object.keys(share).forEach(k => {
    const usd = +(amt * share[k]).toFixed(2);
    if (usd <= 0) return;
    if (blockedSet.has(k)) { held += usd; return; }
    split[k] = usd;
  });
  return {
    amount: +(amt - held).toFixed(2), split, held: +held.toFixed(2), stopped: false,
    xrpArmed: g.xrpArmed === true, blocked: g.blocked || [], phaseKey, deadline: plan.deadline || null,
    onPlanMonth, onTail: !!(plan.tail && onPlanMonth && phaseResult.phase !== 'accumulate'),
    unpriced: g.unpriced || [],
  };
}
